import React from "react";
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardActionArea,
  styled,
  useTheme,
  useMediaQuery,
  Link
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useNavigate } from "react-router-dom";
import Header from "./Header";

// Import images
import awsImage from "../assets/images/light/aws-image.png";
import githubImage from "../assets/images/light/github-image.png";

// Define font family
const fontFamily = '"Proxima Nova", Arial, sans-serif';

const DashboardContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  backgroundColor: "#EEF5F6",
  padding: `${theme.spacing(1)} ${theme.spacing(3)} ${theme.spacing(3)}`,
  display: "flex",
  flexDirection: "column",
  position: "relative",
  overflow: "auto",
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  maxWidth: 1200,
  margin: '0 auto',
  width: '100%',
  padding: theme.spacing(0, 2),
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  margin: '40px 0 24px 90px',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    margin: '20px 0 24px 0',
  },
}));

const PageHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  margin: '0 0 16px 0',
  padding: '0 24px',
}));

const BackLink = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: '#4299D4',
  fontFamily: 'Proxima Nova',
  fontSize: '12px',
  fontWeight: 400,
  lineHeight: 'normal',
  textDecoration: 'none',
  marginLeft: '60px',
  [theme.breakpoints.down('sm')]: {
    marginLeft: '20px',
  },
  '&:hover': {
    textDecoration: 'none',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '12px',
    marginRight: '-1px',
  },
}));

const Breadcrumbs = styled(Box)(({ theme }) => ({
  color: '#4299D4',
  fontFamily: 'Proxima Nova',
  fontSize: '12px',
  fontWeight: 400,
  lineHeight: 'normal',
  textAlign: 'right',
  marginRight: '70px',
  [theme.breakpoints.down('sm')]: {
    marginRight: '20px',
  },
  '& > *': {
    margin: '0 2px',
    '&:first-child': {
      marginLeft: 0,
    },
    '&:last-child': {
      marginRight: 0,
    },
  },
  '& > span': {
    margin: '0 2px',
  },
  '& a': {
    color: 'inherit',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'none',
    },
  },
}));

const Divider = styled('hr')(({ theme }) => ({
  width: '982px',
  height: '1px',
  backgroundColor: '#B1B1B1',
  border: 'none',
  margin: '16px 0',
  [theme.breakpoints.down('lg')]: {
    width: '100%',
  },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  color: '#1674B4',
  fontFamily: 'Proxima Nova',
  fontSize: '30px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: 'normal',
  marginBottom: theme.spacing(1),
}));

const PageDescription = styled(Typography)(({ theme }) => ({
  color: '#2E2E2E',
  fontFamily: 'Proxima Nova',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 'normal',
  marginBottom: theme.spacing(4),
  maxWidth: '800px',
  '& span': {
    fontWeight: 600,
  },
}));

const CardsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: theme.spacing(4),
  marginTop: theme.spacing(-4),
  width: '100%',
  '& > :first-of-type': {
    marginLeft: '90px', // Move first card to the right
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0, // Reset on mobile
    },
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const MonitoringCard = styled(Card)(({ theme }) => ({
  width: '470px',
  height: '124px',
  flexShrink: 0,
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
}));

const CardContentWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  height: '100%',
  padding: '20px',
});

const LogoWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '& img': {
    objectFit: 'contain',
  },
  '&.aws img': {
    width: '80px',
    height: '48.341px',
    aspectRatio: '80/48.34',
  },
  '&.github img': {
    width: '80px',
    height: '78px',
    aspectRatio: '40/39',
  },
  marginRight: '16px',
});

const TextWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
});

const CardContentStyled = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:last-child': {
    paddingBottom: theme.spacing(3),
  },
}));

const CardIcon = styled(Box)(({ theme }) => ({
  width: 56,
  height: 56,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  '&.aws': {
    backgroundColor: 'rgba(255, 153, 0, 0.1)',
    color: theme.palette.warning.main,
  },
  '&.github': {
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
    color: theme.palette.text.primary,
  },
}));

const CardTitle = styled(Typography)({
  color: '#1674B4',
  fontFamily: fontFamily,
  fontSize: '20px',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: 'normal',
  marginBottom: '8px',
});

const CardDescription = styled(Typography)({
  color: '#6D6D6D',
  fontFamily: fontFamily,
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '18px',
});


const monitoringCards = [
  {
    id: 'aws',
    title: 'AWS Monitoring',
    description: 'Track cloud resource usage, costs, and performance across your AWS infrastructure.',
    icon: awsImage,
    path: '/aws-accounts',
    iconClass: 'aws',
  },
  {
    id: 'github',
    title: 'GitHub Monitoring',
    description: 'Monitor repository activity, pull requests, issues, and contributor performance in real time.',
    icon: githubImage,
    path: '/github-dashboard',
    iconClass: 'github',
  },
];

function MonitoringDashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleCardClick = (path) => {
    navigate(path);
  };

  return (
    <>
      <Header />
      <DashboardContainer>
        <ContentContainer>
          <PageHeader>
            <BackLink component={RouterLink} to="/">
              <ArrowBackIosIcon fontSize="small" />
              Back
            </BackLink>
            <Breadcrumbs>
              <Link component={RouterLink} to="/">Dashboard</Link>
              <span>&nbsp;&gt;&nbsp;</span>
              <span style={{ color: '#6D6D6D' }}>Monitoring</span>
            </Breadcrumbs>
          </PageHeader>
          <HeaderContainer>
            <Box>
              <PageTitle>Monitoring</PageTitle>
              <Divider />
              <PageDescription>
                <span>Get real-time visibility into your AWS infrastructure and GitHub activity.</span>
                <br />
                Monitor performance, track resource usage, and stay ahead of system alerts.
              </PageDescription>
            </Box>
          </HeaderContainer>

          <CardsContainer>
            {monitoringCards.map((card) => (
              <MonitoringCard key={card.id} elevation={0}>
                <CardActionArea 
                  onClick={() => handleCardClick(card.path)}
                  sx={{ height: '100%' }}
                >
                  <CardContentWrapper>
                    <LogoWrapper className={card.iconClass}>
                      <img src={card.icon} alt={`${card.title} logo`} />
                    </LogoWrapper>
                    <TextWrapper>
                      <CardTitle>{card.title}</CardTitle>
                      <CardDescription>{card.description}</CardDescription>
                    </TextWrapper>
                  </CardContentWrapper>
                </CardActionArea>
              </MonitoringCard>
            ))}
          </CardsContainer>

        </ContentContainer>
      </DashboardContainer>
    </>
  );
}

export default MonitoringDashboard;
